// src/hooks/useParallax.js
// Tracks mouse position relative to a ref element and returns normalised offsets.
// Values range from -1 to 1 (centre = 0, 0).
// Respects prefers-reduced-motion — returns zero offsets when motion is reduced.
import { useRef, useEffect, useState, useCallback } from 'react';

const prefersReducedMotion =
  typeof window !== 'undefined' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export function useParallax(strength = 1) {
  const ref = useRef(null);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const rafRef = useRef(null);

  const handleMouseMove = useCallback((e) => {
    if (prefersReducedMotion || !ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const nx = ((e.clientX - cx) / (rect.width / 2)) * strength;
    const ny = ((e.clientY - cy) / (rect.height / 2)) * strength;

    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      setOffset({ x: nx, y: ny });
    });
  }, [strength]);

  const handleMouseLeave = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      setOffset({ x: 0, y: 0 });
    });
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el || prefersReducedMotion) return;
    el.addEventListener('mousemove', handleMouseMove);
    el.addEventListener('mouseleave', handleMouseLeave);
    return () => {
      el.removeEventListener('mousemove', handleMouseMove);
      el.removeEventListener('mouseleave', handleMouseLeave);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [handleMouseMove, handleMouseLeave]);

  return { ref, offset };
}
